'use client';

import { useQuery } from '@tanstack/react-query';
import { PublicKey } from '@solana/web3.js';
import { SerializedVaultDepositorRecord } from 'src/db/schema/vault-depositor-records';
import { GET_API_ROUTE } from 'src/constants/api';
import useCurrentAuthority from 'src/hooks/useCurrentAuthority';
import { useVaultDepositorAccounts } from './useVaultDepositorAccounts';
import { VaultDepositorHistoriesLookup } from '../types';
import { VAULT_REFETCH_INTERVAL } from '../consts';
import { vaultQueryKeys } from '../vaultQueryKeys';

async function fetchVaultDepositorHistories(
	authority: PublicKey
): Promise<VaultDepositorHistoriesLookup> {
	const url = `${GET_API_ROUTE(
		'vault-depositor'
	)}?depositorAuthority=${authority.toString()}`;
	const response = await fetch(url);

	if (!response.ok) {
		throw new Error(
			`Failed to fetch vault depositor histories: ${response.status}`
		);
	}

	const data: SerializedVaultDepositorRecord[] = await response.json();

	const lookup: VaultDepositorHistoriesLookup = {};

	data.forEach((record) => {
		const vaultPubkey = record.vault;
		if (!lookup[vaultPubkey]) {
			lookup[vaultPubkey] = [];
		}
		lookup[vaultPubkey].push(record);
	});

	// Sort each vault's history by slot
	Object.keys(lookup).forEach((vaultPubkey) => {
		lookup[vaultPubkey].sort((a, b) => a.slot - b.slot);
	});

	return lookup;
}

export function useVaultDepositorHistories() {
	const authority = useCurrentAuthority();
	const { data: depositorAccounts, isSuccess: hasDepositorAccounts } =
		useVaultDepositorAccounts();

	const hasDeposits =
		hasDepositorAccounts &&
		depositorAccounts &&
		Object.keys(depositorAccounts).length > 0;

	const enabled = !!authority && hasDeposits;

	return useQuery({
		queryKey: vaultQueryKeys.vaultDepositorHistories(authority?.toString()),
		queryFn: async () => {
			if (!authority) {
				return {};
			}
			return fetchVaultDepositorHistories(authority);
		},
		enabled,
		staleTime: VAULT_REFETCH_INTERVAL,
		refetchInterval: VAULT_REFETCH_INTERVAL,
	});
}
